import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/pollen`;

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * LocationAutocomplete — lightweight typeahead for location fields.
 * - Suggests from /api/pollen/locations as user types.
 * - When `autoCreate` is true (default) any brand-new value is POSTed to
 *   /api/pollen/locations on blur OR Enter — so the locations DB grows
 *   universally, not only at tour-save time.
 *
 * Props:
 *  value: current string value
 *  onChange: (newValue:string) => void
 *  onSelect: (locObj) => void  — fires when user picks a saved row OR
 *            when a brand-new row is just created. Receives the full
 *            {id, name, type, lat, lng, ...} so callers can hydrate the
 *            parent form with the existing pin and avoid duplicates.
 *  type: 'city' | 'hotel' | 'route' | '' (all types if empty)
 *  autoCreate: boolean (default true) — set false to opt out of the
 *            blur-create behaviour (e.g. read-only contexts).
 *  placeholder, className, testId
 */
const LocationAutocomplete = ({ value, onChange, onSelect, type = '', placeholder = 'Type or select location', className = 'h-8 text-sm', testId, autoCreate = true }) => {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const lastCreatedRef = useRef(''); // dedup — don't POST the same brand-new value twice in a row

  const tryAutoCreate = async () => {
    if (!autoCreate) return;
    const raw = (value || '').trim();
    if (!raw) return;
    // If the operator pasted a Google Maps URL directly into the field,
    // route it as a *pin* rather than a name: we'll pull the nearest
    // matching city (from existing suggestions or a sibling) and POST
    // /set-coords, which then cascades to every dependent vendor.
    const isMapsUrl = /^https?:\/\/(www\.)?(google\.[a-z.]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(raw);
    if (isMapsUrl) {
      // Find the most recent city we've already surfaced (or, if none,
      // bail — without an anchor name we can't know what to pin).
      const anchor = suggestions.find(s => (s.type || '').toLowerCase() === 'city')
                  || suggestions[0]
                  || (lastCreatedRef.current && { id: null, name: lastCreatedRef.current });
      if (!anchor || !anchor.id) return;
      try {
        const res = await fetch(`${API}/locations/${anchor.id}/set-coords`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ url: raw }),
        });
        if (res.ok) {
          const data = await res.json();
          if (onSelect) onSelect({ ...anchor, lat: data.lat, lng: data.lng, google_maps_url: raw });
          onChange(anchor.name); // replace the URL in the field with the resolved name
        }
      } catch { /* swallow */ }
      return;
    }
    // Skip if value matches an existing suggestion (case-insensitive).
    if (suggestions.some(s => (s.name || '').toLowerCase() === raw.toLowerCase())) return;
    if (lastCreatedRef.current.toLowerCase() === raw.toLowerCase()) return;
    try {
      const res = await fetch(`${API}/locations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: raw, type: type || 'city' }),
      });
      if (!res.ok) return;
      const created = await res.json();
      lastCreatedRef.current = raw;
      if (onSelect) onSelect(created);
    } catch { /* swallow */ }
  };

  useEffect(() => {
    if (!open) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (value) params.append('q', value);
        if (type) params.append('type', type);
        params.append('limit', '10');
        const res = await fetch(`${API}/locations?${params.toString()}`, { credentials: 'include', headers: authHeaders() });
        if (res.ok) {
          setSuggestions(await res.json());
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    }, 150);
    return () => clearTimeout(timerRef.current);
  }, [value, open, type]);

  return (
    <div className="relative">
      <Input
        className={className}
        value={value || ''}
        placeholder={placeholder}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            // Enter commits a brand-new value immediately so the
            // operator gets instant feedback (and the value is reusable
            // on the very next field they fill).
            e.preventDefault();
            tryAutoCreate();
            setOpen(false);
          }
        }}
        onBlur={() => {
          // Slight delay so a click on a suggestion can still fire
          // (the suggestion path already calls onSelect with the real
          // saved row — no need to also POST a duplicate).
          setTimeout(() => { tryAutoCreate(); setOpen(false); }, 180);
        }}
        data-testid={testId}
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-30 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {suggestions.slice(0, 8).map(loc => (
            <button
              key={loc.id}
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-muted text-xs flex items-center justify-between"
              onMouseDown={e => {
                e.preventDefault();
                // Always update the visible string; ALSO propagate the full
                // object so the parent form can carry the existing pin
                // (lat/lng/google_maps_url/id) into save payloads. Without
                // this, picking a saved city from the dropdown silently
                // creates a duplicate Locations DB row on tour save —
                // because the auto-upsert on the server side has no way to
                // tell "I just picked you" from "this is a brand-new name
                // that happens to match an existing one". Real bug, May 2026.
                onChange(loc.name);
                if (onSelect) onSelect(loc);
                setOpen(false);
              }}
              data-testid={`location-opt-${loc.id}`}
            >
              <span className="font-medium truncate flex items-center gap-1.5">
                {(loc.lat != null && loc.lng != null) && (
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" title="Pinned" />
                )}
                {loc.name}
              </span>
              <span className="text-[10px] text-muted-foreground ml-2 shrink-0">
                {loc.type}{loc.usage_count > 1 ? ` · ${loc.usage_count}×` : ''}
              </span>
            </button>
          ))}
        </div>
      )}
      {open && !loading && suggestions.length === 0 && value && (
        <div className="absolute z-30 w-full mt-1 bg-white border rounded-lg shadow-lg text-[11px] text-muted-foreground px-3 py-2">
          {autoCreate ? <>New location — saved when you press Enter or move on</> : <>New location — will be saved on tour save</>}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
